import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../../contexts/AuthContext";
import { Typography, Box, CircularProgress, Grid } from "@mui/material";
import { People as PeopleIcon } from "@mui/icons-material";
import RelatedDiveCard from "./cards/RelatedDiveCard";
import RelatedDiveGroupDialog from "./dialogs/RelatedDiveGroupDialog";

/**
 * Component for displaying dives that are related to the user's dives
 */
function UserRelatedDives() {
  const [relatedDives, setRelatedDives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const { currentUser } = useAuth();

  useEffect(() => {
    const fetchRelatedDives = async () => {
      if (currentUser) {
        try {
          setLoading(true);
          const token = await currentUser.getIdToken();
          const response = await axios.get(
            `/api/dives/related`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
          setRelatedDives(response.data.relatedDives);
        } catch (err) {
          console.error("Error fetching related dives:", err);
          setError("Failed to load related dives");
        } finally {
          setLoading(false);
        }
      }
    };

    fetchRelatedDives();
  }, [currentUser]);

  const handleViewGroup = async (relatedGroupId) => {
    try {
      const token = await currentUser.getIdToken();
      const response = await axios.get(
        `/api/dives/shared/related/${relatedGroupId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setSelectedGroup(response.data);
      setDialogOpen(true);
    } catch (err) {
      console.error("Error fetching related dive group:", err);
    }
  };

  if (loading) {
    return (
      <CircularProgress
        size={24}
        sx={{ display: "block", mx: "auto", my: 2 }}
      />
    );
  }

  if (error) {
    return (
      <Typography color="error" align="center">
        {error}
      </Typography>
    );
  }

  if (relatedDives.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 3 }}>
        <PeopleIcon
          sx={{ fontSize: 48, color: "text.secondary", mb: 2, opacity: 0.5 }}
        />
        <Typography color="textSecondary">
          You don't have any related dives yet.
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          When other divers share their dives with you, they will appear here.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        These are dives from other divers related to your dive logs.
      </Typography>

      <Grid container spacing={2}>
        {relatedDives.map((dive) => (
          <Grid item xs={12} sm={6} md={4} key={dive._id}>
            <RelatedDiveCard
              dive={dive}
              onViewDetails={() => handleViewGroup(dive.relatedGroupId)}
            />
          </Grid>
        ))}
      </Grid>

      {selectedGroup && (
        <RelatedDiveGroupDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          group={selectedGroup}
        />
      )}
    </>
  );
}

export default UserRelatedDives;
